import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Fieldset,
  Heading,
  Paragraph,
  Radio,
} from '@digdir/designsystemet-react';
import { useExtendEvaluationDeadlineMutation } from '../../../hooks/mutations/useWorkEnvironmentAssessmentMutations';
import { RESPONSE_DAYS } from '../evaluationView';
import process from '../WorkEnvironmentSurveyProcess.module.css';

type Props = {
  surveyId: number;
  evaluationId: number;
  responses: number;
  canExtend: boolean;
};

const DAY_MS = 24 * 60 * 60 * 1000;

/** Too few answered the evaluation to compare. The team gets more time, as with the first survey. */
export const InsufficientEvaluationCard = ({
  surveyId,
  evaluationId,
  responses,
  canExtend,
}: Props): ReactElement => {
  const { t } = useTranslation();
  const [days, setDays] = useState<number>(RESPONSE_DAYS[0]);
  const extend = useExtendEvaluationDeadlineMutation(surveyId, evaluationId);
  return (
    <section
      aria-labelledby="evaluation-insufficient-heading"
      className={process.card}
    >
      <div className={process.cardBody}>
        <Heading level={2} data-size="sm" id="evaluation-insufficient-heading">
          {t('workEnvironmentEvaluation.insufficient.title')}
        </Heading>
        <Paragraph data-size="sm">
          {t('workEnvironmentEvaluation.insufficient.text', {
            count: responses,
          })}
        </Paragraph>
        {canExtend && (
          <Fieldset data-size="sm">
            <Fieldset.Legend>
              {t('workEnvironmentEvaluation.insufficient.deadline')}
            </Fieldset.Legend>
            {RESPONSE_DAYS.map((option) => (
              <Radio
                key={option}
                name="evaluation-extension"
                label={t('workEnvironmentEvaluation.start.days', {
                  count: option,
                })}
                value={String(option)}
                checked={days === option}
                onChange={() => setDays(option)}
              />
            ))}
          </Fieldset>
        )}
        {extend.isError && (
          <Alert data-color="danger" data-size="sm">
            <Paragraph data-size="sm">
              {t('workEnvironmentEvaluation.insufficient.failed')}
            </Paragraph>
          </Alert>
        )}
      </div>
      {canExtend && (
        <div className={process.cardFooter}>
          <Button
            variant="primary"
            loading={extend.isPending}
            onClick={() =>
              extend.mutate(new Date(Date.now() + days * DAY_MS).toISOString())
            }
          >
            {t('workEnvironmentEvaluation.insufficient.extend')}
          </Button>
        </div>
      )}
    </section>
  );
};
